import React, { useState, useEffect, useRef } from 'react';
import { 
  LayoutDashboard, Music, Calendar, ArrowRight, Plus, Trash2, 
  Edit3, Save, X, Upload, Search, Clock, User, FileText,
  ChevronDown, AlertCircle, Check
} from 'lucide-react';

/**
 * لوحة التحكم - Dashboard
 * إدارة جدول البرامج والمكتبة الموسيقية
 */
const Dashboard = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('programs');
  const [notification, setNotification] = useState(null);

  // ===== إدارة البرامج =====
  const [programs, setPrograms] = useState([]);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [programSearch, setProgramSearch] = useState('');
  const [selectedDayFilter, setSelectedDayFilter] = useState('all');

  // ===== إدارة المكتبة الموسيقية =====
  const [songs, setSongs] = useState([]);
  const [editingSong, setEditingSong] = useState(null);
  const [showSongForm, setShowSongForm] = useState(false);
  const [songSearch, setSongSearch] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const fileInputRef = useRef(null);

  const days = ['السبت', 'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'];
  const categories = [
    { id: 'sheikhs', label: 'أغاني مهداة للشيوخ' },
    { id: 'national', label: 'أغاني وطنية' },
    { id: 'special', label: 'أغاني خاصة' },
    { id: 'poems', label: 'قصائد وطنية' },
  ];

  // تحميل البيانات من localStorage
  useEffect(() => {
    const savedPrograms = localStorage.getItem('dashboard_programs');
    if (savedPrograms) {
      try {
        setPrograms(JSON.parse(savedPrograms));
      } catch (e) {
        console.error('Error loading programs:', e);
      }
    } else {
      // بيانات افتراضية
      const defaultPrograms = [
        { id: 1, titleAr: 'بودكاست: يجمعنا وطن', time: '06:00 - 08:00', presenter: 'فريق الإذاعة', description: 'يركز على إنجازات الجامعة والقوات المسلحة', day: 'السبت' },
        { id: 2, titleAr: 'بودكاست: للوطن كلمة ', time: '08:00 - 12:00', presenter: 'فريق الإذاعة', description: 'البث المباشر احتفالاً بالعيد الوطني 54', day: 'السبت', isLive: true },
        { id: 3, titleAr: 'برنامج: التكامل والموائمة', time: '12:00 - 14:00', presenter: 'فريق الإذاعة', description: 'يركز على الجانب العلمي والأكاديمي', day: 'السبت' },
        { id: 4, titleAr: 'بودكاست: التكامل والموائمة', time: '14:00 - 16:00', presenter: 'فريق الإذاعة', description: 'التعرف على نماذج ناجحة من المرشحين', day: 'الأحد' },
        { id: 5, titleAr: 'برنامج: التكامل والموائمة', time: '16:00 - 18:00', presenter: 'فريق الإذاعة', description: 'يركز على الاستراتيجيات والسياسات', day: 'الأحد' },
        { id: 6, titleAr: 'زايد الأرث', time: '18:00 - 19:00', presenter: 'فريق الإذاعة', description: 'فاصل يركز على إرث زايد الإنساني والوطني', day: 'الإثنين' },
      ];
      setPrograms(defaultPrograms);
      localStorage.setItem('dashboard_programs', JSON.stringify(defaultPrograms));
    }

    const savedSongs = localStorage.getItem('dashboard_songs');
    if (savedSongs) {
      try {
        setSongs(JSON.parse(savedSongs));
      } catch (e) {
        console.error('Error loading songs:', e);
      }
    } else {
      const defaultSongs = [
        { id: 1, titleAr: 'عيشي بلادي', category: 'national', duration: '3:50', description: 'أغنية وطنية إماراتية', addedAt: new Date().toISOString() },
        { id: 2, titleAr: 'أنا العلم - بلقيس فتحي', category: 'national', duration: '3:45', description: 'النشيد الوطني لبلقيس فتحي', addedAt: new Date().toISOString() },
        { id: 3, titleAr: 'يا عملنا - بلقيس فتحي وحسين الجسمي', category: 'sheikhs', duration: '4:20', description: 'أغنية وطنية بالتعاون بين فنانين', addedAt: new Date().toISOString() },
        { id: 4, titleAr: 'يا عملنا للأطفال', category: 'special', duration: '3:30', description: 'نسخة الأطفال من يا عملنا', addedAt: new Date().toISOString() },
        { id: 5, titleAr: 'يحيا علم اليوم يومك يا علم', category: 'national', duration: '4:00', description: 'أغنية احتفال بالعلم الوطني', addedAt: new Date().toISOString() },
      ];
      setSongs(defaultSongs);
      localStorage.setItem('dashboard_songs', JSON.stringify(defaultSongs));
    }
  }, []);

  // حفظ البيانات عند التغيير
  useEffect(() => {
    if (programs.length > 0) {
      localStorage.setItem('dashboard_programs', JSON.stringify(programs));
    }
  }, [programs]);

  useEffect(() => {
    if (songs.length > 0) {
      localStorage.setItem('dashboard_songs', JSON.stringify(songs));
    }
  }, [songs]);

  // إظهار إشعار
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // ===== وظائف البرامج =====
  const emptyProgram = { titleAr: '', time: '', presenter: '', description: '', day: 'السبت', isLive: false };

  const handleSaveProgram = (programData) => {
    if (!programData.titleAr || !programData.time || !programData.day) {
      showNotification('يرجى ملء جميع الحقول المطلوبة', 'error');
      return;
    }

    if (editingProgram) {
      setPrograms(prev => prev.map(p => p.id === editingProgram.id ? { ...programData, id: editingProgram.id } : p));
      showNotification('تم تحديث البرنامج بنجاح');
    } else {
      const newProgram = { ...programData, id: Date.now() };
      setPrograms(prev => [...prev, newProgram]);
      showNotification('تم إضافة البرنامج بنجاح');
    }
    setEditingProgram(null);
    setShowProgramForm(false);
  };

  const handleDeleteProgram = (id) => {
    setPrograms(prev => prev.filter(p => p.id !== id));
    showNotification('تم حذف البرنامج');
  };

  const filteredPrograms = programs.filter(p => {
    const matchesSearch = p.titleAr.includes(programSearch) || p.presenter.includes(programSearch);
    const matchesDay = selectedDayFilter === 'all' || p.day === selectedDayFilter;
    return matchesSearch && matchesDay;
  });

  // ===== وظائف الأغاني =====
  const emptySong = { titleAr: '', category: 'national', duration: '', description: '', fileName: '' };

  const handleSaveSong = (songData) => {
    if (!songData.titleAr || !songData.category) {
      showNotification('يرجى ملء جميع الحقول المطلوبة', 'error');
      return;
    }

    if (editingSong) {
      setSongs(prev => prev.map(s => s.id === editingSong.id ? { ...songData, id: editingSong.id, addedAt: editingSong.addedAt } : s));
      showNotification('تم تحديث الأغنية بنجاح');
    } else {
      const newSong = { ...songData, id: Date.now(), addedAt: new Date().toISOString() };
      setSongs(prev => [...prev, newSong]);
      showNotification('تم إضافة الأغنية بنجاح');
    }
    setEditingSong(null);
    setShowSongForm(false);
  };

  const handleDeleteSong = (id) => {
    setSongs(prev => prev.filter(s => s.id !== id));
    showNotification('تم حذف الأغنية');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('audio/')) {
        showNotification(`تم تحميل الملف: ${file.name}`);
        return file.name;
      } else {
        showNotification('يرجى اختيار ملف صوتي', 'error');
      }
    }
    return '';
  };

  const filteredSongs = songs.filter(s => {
    const matchesSearch = s.titleAr.includes(songSearch) || s.description?.includes(songSearch);
    const matchesCategory = selectedCategoryFilter === 'all' || s.category === selectedCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryLabel = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.label : catId;
  };

  // ===== مكون النموذج =====
  const ProgramForm = ({ initial, onSave, onCancel }) => {
    const [form, setForm] = useState(initial || emptyProgram);
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-primary to-primary-dark p-5 flex items-center justify-between">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {initial ? 'تعديل البرنامج' : 'إضافة برنامج جديد'}
            </h3>
            <button onClick={onCancel} className="text-white/70 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* اسم البرنامج */}
            <div>
              <label className="block text-primary font-bold text-sm mb-1">اسم البرنامج *</label>
              <input
                type="text"
                value={form.titleAr}
                onChange={e => setForm({ ...form, titleAr: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary"
                placeholder="أدخل اسم البرنامج"
              />
            </div>

            {/* الوقت واليوم */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-primary font-bold text-sm mb-1">الوقت *</label>
                <input
                  type="text"
                  value={form.time}
                  onChange={e => setForm({ ...form, time: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary"
                  placeholder="06:00 - 08:00"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-primary font-bold text-sm mb-1">اليوم *</label>
                <div className="relative">
                  <select
                    value={form.day}
                    onChange={e => setForm({ ...form, day: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary appearance-none"
                  >
                    {days.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-beige-dark pointer-events-none" />
                </div>
              </div>
            </div>

            {/* المقدم */}
            <div>
              <label className="block text-primary font-bold text-sm mb-1">المقدم</label>
              <input
                type="text"
                value={form.presenter}
                onChange={e => setForm({ ...form, presenter: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary"
                placeholder="اسم المقدم"
              />
            </div>

            {/* الوصف */}
            <div>
              <label className="block text-primary font-bold text-sm mb-1">الوصف</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary resize-none"
                rows={3}
                placeholder="وصف البرنامج"
              />
            </div>

            {/* بث مباشر */}
            <label className="flex items-center gap-3 cursor-pointer">
              <div className={`w-12 h-6 rounded-full transition-colors duration-300 relative ${form.isLive ? 'bg-gold' : 'bg-beige'}`}>
                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all duration-300 shadow-md ${form.isLive ? 'right-0.5' : 'right-[calc(100%-22px)]'}`}></div>
              </div>
              <span className="text-primary font-bold text-sm">بث مباشر</span>
            </label>

            {/* أزرار */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => onSave(form)}
                className="flex-1 bg-gradient-to-r from-gold to-gold-light text-white font-bold py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                <span>حفظ</span>
              </button>
              <button
                onClick={onCancel}
                className="px-6 bg-beige/30 text-primary font-bold py-3 rounded-xl hover:bg-beige/50 transition-all duration-300"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SongForm = ({ initial, onSave, onCancel }) => {
    const [form, setForm] = useState(initial || emptySong);
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-gold-dark to-gold p-5 flex items-center justify-between">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <Music className="w-5 h-5" />
              {initial ? 'تعديل الأغنية' : 'إضافة أغنية جديدة'}
            </h3>
            <button onClick={onCancel} className="text-white/70 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* اسم الأغنية */}
            <div>
              <label className="block text-primary font-bold text-sm mb-1">اسم الأغنية *</label>
              <input
                type="text"
                value={form.titleAr}
                onChange={e => setForm({ ...form, titleAr: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary"
                placeholder="أدخل اسم الأغنية"
              />
            </div>

            {/* التصنيف والمدة */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-primary font-bold text-sm mb-1">التصنيف *</label>
                <div className="relative">
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary appearance-none"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-beige-dark pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-primary font-bold text-sm mb-1">المدة</label>
                <input
                  type="text"
                  value={form.duration}
                  onChange={e => setForm({ ...form, duration: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary"
                  placeholder="3:50"
                  dir="ltr"
                />
              </div>
            </div>

            {/* الوصف */}
            <div>
              <label className="block text-primary font-bold text-sm mb-1">الوصف</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-beige focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all bg-lightBeige-light/30 text-primary resize-none"
                rows={3}
                placeholder="وصف الأغنية"
              />
            </div>

            {/* تحميل الملف */}
            <div>
              <label className="block text-primary font-bold text-sm mb-1">الملف الصوتي</label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-4 py-6 rounded-xl border-2 border-dashed border-beige hover:border-gold cursor-pointer transition-all bg-lightBeige-light/20 flex flex-col items-center gap-2 group"
              >
                <Upload className="w-8 h-8 text-beige-dark group-hover:text-gold transition-colors" />
                <span className="text-beige-dark group-hover:text-gold text-sm font-bold transition-colors">
                  {form.fileName || 'اضغط لتحميل ملف صوتي'}
                </span>
                <span className="text-beige text-xs">MP3, WAV, OGG</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={(e) => {
                  const fileName = handleFileUpload(e);
                  if (fileName) setForm({ ...form, fileName });
                }}
                className="hidden"
              />
            </div>

            {/* أزرار */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => onSave(form)}
                className="flex-1 bg-gradient-to-r from-gold to-gold-light text-white font-bold py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                <span>حفظ</span>
              </button>
              <button
                onClick={onCancel}
                className="px-6 bg-beige/30 text-primary font-bold py-3 rounded-xl hover:bg-beige/50 transition-all duration-300"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-lightBeige via-white to-lightBeige-light" dir="rtl">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-6 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-slide-in-up ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-gradient-to-r from-gold to-gold-light text-white'
        }`}>
          {notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
          <span className="font-bold text-sm">{notification.message}</span>
        </div>
      )}

      {/* Forms */}
      {showProgramForm && (
        <ProgramForm
          initial={editingProgram}
          onSave={handleSaveProgram}
          onCancel={() => { setShowProgramForm(false); setEditingProgram(null); }}
        />
      )}
      {showSongForm && (
        <SongForm
          initial={editingSong}
          onSave={handleSaveSong}
          onCancel={() => { setShowSongForm(false); setEditingSong(null); }}
        />
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-primary via-primary-dark to-primary shadow-2xl sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Right: Title */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gold to-gold-light rounded-xl flex items-center justify-center shadow-lg">
              <LayoutDashboard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">hhhhhhh التحكم</h1>
              <p className="text-gold/70 text-xs">إذاعة جامعة زايد العسكرية</p>
            </div>
          </div>

          {/* Center: Tabs */}
          <div className="hidden md:flex items-center gap-1 bg-white/10 backdrop-blur-sm rounded-xl p-1">
            <button
              onClick={() => setActiveTab('programs')}
              className={`px-5 py-2 rounded-lg font-bold text-sm transition-all duration-300 flex items-center gap-2 ${
                activeTab === 'programs'
                  ? 'bg-gradient-to-r from-gold to-gold-light text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>جدول البرامج</span>
            </button>
            <button
              onClick={() => setActiveTab('music')}
              className={`px-5 py-2 rounded-lg font-bold text-sm transition-all duration-300 flex items-center gap-2 ${
                activeTab === 'music'
                  ? 'bg-gradient-to-r from-gold to-gold-light text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Music className="w-4 h-4" />
              <span>المكتبة الموسيقية</span>
            </button>
          </div>

          {/* Left: Back Button */}
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gold hover:text-gold-light transition-colors font-bold text-sm bg-white/10 px-4 py-2 rounded-xl hover:bg-white/20"
          >
            <span>العودة للموقع</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile Tabs */}
        <div className="md:hidden flex items-center gap-1 px-4 pb-3">
          <button
            onClick={() => setActiveTab('programs')}
            className={`flex-1 px-4 py-2 rounded-lg font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
              activeTab === 'programs'
                ? 'bg-gradient-to-r from-gold to-gold-light text-white shadow-lg'
                : 'text-white/70 hover:text-white bg-white/10'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span>البرامج</span>
          </button>
          <button
            onClick={() => setActiveTab('music')}
            className={`flex-1 px-4 py-2 rounded-lg font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
              activeTab === 'music'
                ? 'bg-gradient-to-r from-gold to-gold-light text-white shadow-lg'
                : 'text-white/70 hover:text-white bg-white/10'
            }`}
          >
            <Music className="w-4 h-4" />
            <span>المكتبة</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-lg border border-gold/20 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <span className="text-beige-dark text-sm font-bold">البرامج</span>
            </div>
            <p className="text-3xl font-bold text-primary">{programs.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-lg border border-gold/20 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-gold to-gold-light rounded-xl flex items-center justify-center">
                <Music className="w-5 h-5 text-white" />
              </div>
              <span className="text-beige-dark text-sm font-bold">الأغاني</span>
            </div>
            <p className="text-3xl font-bold text-primary">{songs.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-lg border border-gold/20 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-beige-dark to-beige rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <span className="text-beige-dark text-sm font-bold">بث مباشر</span>
            </div>
            <p className="text-3xl font-bold text-primary">{programs.filter(p => p.isLive).length}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-lg border border-gold/20 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-gold-dark to-primary rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="text-beige-dark text-sm font-bold">التصنيفات</span>
            </div>
            <p className="text-3xl font-bold text-primary">{categories.length}</p>
          </div>
        </div>

        {/* ===== Programs Tab ===== */}
        {activeTab === 'programs' && (
          <div className="animate-fade-in">
            {/* Toolbar */}
            <div className="bg-white rounded-2xl shadow-lg border border-gold/20 p-4 mb-6">
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-beige-dark" />
                  <input
                    type="text"
                    value={programSearch}
                    onChange={e => setProgramSearch(e.target.value)}
                    className="w-full pr-10 pl-4 py-3 rounded-xl border-2 border-beige/50 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all text-primary bg-lightBeige-light/20"
                    placeholder="بحث في البرامج..."
                  />
                </div>

                {/* Day Filter */}
                <div className="relative">
                  <select
                    value={selectedDayFilter}
                    onChange={e => setSelectedDayFilter(e.target.value)}
                    className="px-4 py-3 rounded-xl border-2 border-beige/50 focus:border-gold outline-none transition-all text-primary bg-lightBeige-light/20 appearance-none pr-4 pl-10 font-bold text-sm"
                  >
                    <option value="all">جميع الأيام</option>
                    {days.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-beige-dark pointer-events-none" />
                </div>

                {/* Add Button */}
                <button
                  onClick={() => { setEditingProgram(null); setShowProgramForm(true); }}
                  className="bg-gradient-to-r from-gold to-gold-light text-white font-bold px-6 py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Plus className="w-5 h-5" />
                  <span>إضافة برنامج</span>
                </button>
              </div>
            </div>

            {/* Programs Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gold/20 overflow-hidden">
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 bg-gradient-to-r from-primary to-primary-dark text-white p-4 font-bold text-sm">
                <div className="col-span-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span>اسم البرنامج</span>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>الوقت</span>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>اليوم</span>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>المقدم</span>
                </div>
                <div className="col-span-1 text-center">الحالة</div>
                <div className="col-span-2 text-center">الإجراءات</div>
              </div>

              {/* Table Rows */}
              {filteredPrograms.length === 0 ? (
                <div className="p-12 text-center">
                  <Calendar className="w-16 h-16 text-beige mx-auto mb-4" />
                  <p className="text-beige-dark font-bold text-lg">لا توجد برامج</p>
                  <p className="text-beige text-sm mt-1">اضغط على "إضافة برنامج" للبدء</p>
                </div>
              ) : (
                <div className="divide-y divide-beige/20">
                  {filteredPrograms.map((program, index) => (
                    <div
                      key={program.id}
                      className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 p-4 items-center hover:bg-lightBeige-light/30 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-lightBeige-light/10'
                      }`}
                    >
                      {/* اسم البرنامج */}
                      <div className="md:col-span-3">
                        <div className="flex items-center gap-2 md:gap-0">
                          <span className="md:hidden text-beige-dark font-bold text-xs min-w-[60px]">البرنامج:</span>
                          <p className="font-bold text-primary text-sm">{program.titleAr}</p>
                        </div>
                        {program.description && (
                          <p className="text-beige-dark text-xs mt-1 line-clamp-1 hidden md:block">{program.description}</p>
                        )}
                      </div>

                      {/* الوقت */}
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-2">
                          <span className="md:hidden text-beige-dark font-bold text-xs min-w-[60px]">الوقت:</span>
                          <span className="text-sm text-primary bg-lightBeige/50 px-2 py-1 rounded-lg" dir="ltr">{program.time}</span>
                        </div>
                      </div>

                      {/* اليوم */}
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-2">
                          <span className="md:hidden text-beige-dark font-bold text-xs min-w-[60px]">اليوم:</span>
                          <span className="text-sm font-bold text-primary bg-gold/10 px-3 py-1 rounded-lg border border-gold/20">{program.day}</span>
                        </div>
                      </div>

                      {/* المقدم */}
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-2">
                          <span className="md:hidden text-beige-dark font-bold text-xs min-w-[60px]">المقدم:</span>
                          <span className="text-sm text-beige-dark">{program.presenter}</span>
                        </div>
                      </div>

                      {/* الحالة */}
                      <div className="md:col-span-1 flex items-center gap-2 md:justify-center">
                        <span className="md:hidden text-beige-dark font-bold text-xs min-w-[60px]">الحالة:</span>
                        {program.isLive ? (
                          <span className="text-xs font-bold bg-gradient-to-r from-gold to-gold-light text-white px-3 py-1 rounded-full flex items-center gap-1 w-fit">
                            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                            مباشر
                          </span>
                        ) : (
                          <span className="text-xs font-bold bg-beige/20 text-beige-dark px-3 py-1 rounded-full w-fit">مسجل</span>
                        )}
                      </div>

                      {/* الإجراءات */}
                      <div className="md:col-span-2 flex items-center gap-2 justify-end md:justify-center mt-2 md:mt-0">
                        <button
                          onClick={() => { setEditingProgram(program); setShowProgramForm(true); }}
                          className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all duration-300"
                          title="تعديل"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProgram(program.id)}
                          className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-300"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== Music Tab ===== */}
        {activeTab === 'music' && (
          <div className="animate-fade-in">
            {/* Toolbar */}
            <div className="bg-white rounded-2xl shadow-lg border border-gold/20 p-4 mb-6">
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-beige-dark" />
                  <input
                    type="text"
                    value={songSearch}
                    onChange={e => setSongSearch(e.target.value)}
                    className="w-full pr-10 pl-4 py-3 rounded-xl border-2 border-beige/50 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all text-primary bg-lightBeige-light/20"
                    placeholder="بحث في الأغاني..."
                  />
                </div>

                {/* Category Filter */}
                <div className="relative">
                  <select
                    value={selectedCategoryFilter}
                    onChange={e => setSelectedCategoryFilter(e.target.value)}
                    className="px-4 py-3 rounded-xl border-2 border-beige/50 focus:border-gold outline-none transition-all text-primary bg-lightBeige-light/20 appearance-none pr-4 pl-10 font-bold text-sm"
                  >
                    <option value="all">جميع التصنيفات</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-beige-dark pointer-events-none" />
                </div>

                {/* Add Button */}
                <button
                  onClick={() => { setEditingSong(null); setShowSongForm(true); }}
                  className="bg-gradient-to-r from-gold to-gold-light text-white font-bold px-6 py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Plus className="w-5 h-5" />
                  <span>إضافة أغنية</span>
                </button>
              </div>
            </div>

            {/* Songs Grid */}
            {filteredSongs.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gold/20 p-12 text-center">
                <Music className="w-16 h-16 text-beige mx-auto mb-4" />
                <p className="text-beige-dark font-bold text-lg">لا توجد أغاني</p>
                <p className="text-beige text-sm mt-1">اضغط على "إضافة أغنية" للبدء</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSongs.map((song) => (
                  <div
                    key={song.id}
                    className="bg-white rounded-2xl shadow-lg border border-gold/20 overflow-hidden hover:shadow-xl hover:border-gold/40 transition-all duration-300 group"
                  >
                    {/* Song Header Gradient */}
                    <div className={`h-2 ${
                      song.category === 'sheikhs' ? 'bg-gradient-to-r from-gold to-gold-light' :
                      song.category === 'national' ? 'bg-gradient-to-r from-primary to-primary-light' :
                      song.category === 'special' ? 'bg-gradient-to-r from-gold-dark to-primary' :
                      'bg-gradient-to-r from-beige-dark to-primary'
                    }`}></div>

                    <div className="p-5">
                      {/* Title & Category */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-bold text-primary text-base group-hover:text-gold transition-colors">{song.titleAr}</h4>
                          <span className={`inline-block mt-1 text-xs font-bold px-3 py-1 rounded-full ${
                            song.category === 'sheikhs' ? 'bg-gold/10 text-gold-dark border border-gold/20' :
                            song.category === 'national' ? 'bg-primary/10 text-primary border border-primary/20' :
                            song.category === 'special' ? 'bg-gold-dark/10 text-gold-dark border border-gold-dark/20' :
                            'bg-beige/20 text-beige-dark border border-beige/30'
                          }`}>
                            {getCategoryLabel(song.category)}
                          </span>
                        </div>
                        <div className="w-10 h-10 bg-gradient-to-br from-gold/20 to-primary/20 rounded-xl flex items-center justify-center">
                          <Music className="w-5 h-5 text-gold" />
                        </div>
                      </div>

                      {/* Description */}
                      {song.description && (
                        <p className="text-beige-dark text-sm mb-3 line-clamp-2">{song.description}</p>
                      )}

                      {/* Meta Info */}
                      <div className="flex items-center gap-3 mb-4">
                        {song.duration && (
                          <span className="text-xs text-beige-dark flex items-center gap-1 bg-lightBeige/50 px-2 py-1 rounded-lg">
                            <Clock className="w-3 h-3" />
                            {song.duration}
                          </span>
                        )}
                        {song.fileName && (
                          <span className="text-xs text-beige-dark flex items-center gap-1 bg-lightBeige/50 px-2 py-1 rounded-lg truncate max-w-[150px]">
                            <Upload className="w-3 h-3" />
                            {song.fileName}
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-3 border-t border-beige/20">
                        <button
                          onClick={() => { setEditingSong(song); setShowSongForm(true); }}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all duration-300 font-bold text-sm"
                        >
                          <Edit3 className="w-4 h-4" />
                          <span>تعديل</span>
                        </button>
                        <button
                          onClick={() => handleDeleteSong(song.id)}
                          className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-300 font-bold text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>حذف</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-8 py-6 bg-gradient-to-r from-primary via-primary-dark to-primary text-center">
        <p className="text-gold/70 text-sm font-bold">
          لوحة تحكم إذاعة جامعة زايد العسكرية © {new Date().getFullYear()}
        </p>
      </footer>
    </div>
  );
};

export default Dashboard;
